import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CenterSchedule, getColorStyle } from '../contexts/CenterScheduleContext';

interface ScheduleCalendarProps {
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  schedules: CenterSchedule[];
  loading?: boolean;
  onDateClick?: (dateStr: string) => void;
}

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  currentMonth,
  onMonthChange,
  schedules,
  loading = false,
  onDateClick,
}) => {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const prevMonth = () => onMonthChange(new Date(year, month - 1, 1));
  const nextMonth = () => onMonthChange(new Date(year, month + 1, 1));

  const formatDate = (day: number) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const isToday = (day: number) => {
    const d = new Date(year, month, day);
    return d.getTime() === today.getTime();
  };

  const getSchedulesForDate = (dateStr: string) =>
    schedules.filter(s => s.event_date === dateStr);

  // 팝업 상태
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const selectedSchedules = selectedDate ? getSchedulesForDate(selectedDate) : [];

  const handleDateClick = (dateStr: string) => {
    const daySchedules = getSchedulesForDate(dateStr);
    if (daySchedules.length > 0) {
      setSelectedDate(dateStr);
    }
    if (onDateClick) onDateClick(dateStr);
  };

  const closePopup = () => setSelectedDate(null);

  const formatSelectedDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    const dayName = DAY_NAMES[d.getDay()];
    return `${d.getMonth() + 1}월 ${d.getDate()}일 (${dayName})`;
  };

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= lastDate; d++) cells.push(d);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* 헤더: 월 이동 */}
      <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b">
        <button
          onClick={prevMonth}
          className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
        </button>
        <h3 className="text-lg font-bold text-gray-800">
          {year}년 {month + 1}월
        </h3>
        <button
          onClick={nextMonth}
          className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ChevronRightIcon className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* 범례 */}
      <div className="flex items-center justify-end gap-4 px-6 py-2 text-xs text-gray-500 border-b">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-red-100 border border-red-300"></span>
          <span>휴관일</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-green-100 border border-green-300"></span>
          <span>일정</span>
        </span>
      </div>

      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 border-b">
        {DAY_NAMES.map((name, i) => (
          <div
            key={name}
            className={`py-3 text-center text-sm font-semibold ${
              i === 0 ? 'text-red-400 bg-red-50/50' : i === 6 ? 'text-blue-400 bg-blue-50/50' : 'text-gray-600 bg-gray-50/50'
            }`}
          >
            {name}
          </div>
        ))}
      </div>

      {/* 날짜 그리드 */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-400 text-sm">로딩 중...</div>
        </div>
      ) : (
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            if (day === null) {
              return (
                <div
                  key={`empty-${idx}`}
                  className="min-h-[90px] sm:min-h-[110px] border-b border-r border-gray-100"
                />
              );
            }

            const dateStr = formatDate(day);
            const daySchedules = getSchedulesForDate(dateStr);
            const dayOfWeek = (firstDay + day - 1) % 7;
            const hasHoliday = daySchedules.some(s => s.is_holiday);

            return (
              <div
                key={day}
                onClick={() => handleDateClick(dateStr)}
                className={`min-h-[90px] sm:min-h-[110px] border-b border-r border-gray-100 p-1.5 sm:p-2 transition-all ${
                  hasHoliday ? 'bg-red-50/40' : 'bg-white'
                } ${daySchedules.length > 0 ? 'cursor-pointer hover:bg-gray-50' : ''}`}
              >
                <div className="flex flex-col h-full">
                  {/* 날짜 숫자 */}
                  <span
                    className={`text-sm sm:text-base font-medium mb-1 ${
                      isToday(day) ? 'bg-green-600 text-white w-7 h-7 rounded-full flex items-center justify-center' : ''
                    } ${
                      !isToday(day) && hasHoliday ? 'text-red-500' :
                      !isToday(day) && dayOfWeek === 0 ? 'text-red-400' :
                      !isToday(day) && dayOfWeek === 6 ? 'text-blue-400' :
                      !isToday(day) ? 'text-gray-700' : ''
                    }`}
                  >
                    {day}
                  </span>

                  {/* 일정 목록 */}
                  <div className="flex flex-col gap-0.5 mt-auto">
                    {daySchedules.slice(0, 3).map(schedule => {
                      const cs = schedule.is_holiday
                        ? getColorStyle('red')
                        : getColorStyle(schedule.color);
                      return (
                        <div
                          key={schedule.id}
                          className={`text-[10px] sm:text-xs px-1.5 py-0.5 rounded truncate font-medium ${cs.bg} ${cs.text}`}
                          title={schedule.title}
                        >
                          {schedule.title}
                        </div>
                      );
                    })}
                    {daySchedules.length > 3 && (
                      <span className="text-[10px] text-gray-400 px-1">
                        +{daySchedules.length - 3}개 더
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* 날짜 클릭 팝업 */}
      {selectedDate && selectedSchedules.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={closePopup}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-[90%] max-w-sm mx-4 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* 팝업 헤더 */}
            <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50">
              <h4 className="text-lg font-bold text-gray-800">
                {formatSelectedDate(selectedDate)}
              </h4>
              <button
                onClick={closePopup}
                className="p-1 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* 팝업 내용 */}
            <div className="px-5 py-4 space-y-2.5 max-h-[60vh] overflow-y-auto">
              {selectedSchedules.map(schedule => {
                const cs = schedule.is_holiday
                  ? getColorStyle('red')
                  : getColorStyle(schedule.color);
                return (
                  <div
                    key={schedule.id}
                    className={`px-3.5 py-2.5 rounded-lg text-sm font-medium border ${cs.bg} ${cs.text} ${cs.border}`}
                  >
                    {schedule.title}
                  </div>
                );
              })}
            </div>

            {/* 닫기 버튼 */}
            <div className="px-5 py-3 border-t bg-gray-50">
              <button
                onClick={closePopup}
                className="w-full py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleCalendar;
